import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Typography, Box, Alert, AlertTitle, ToggleButton, ToggleButtonGroup, CircularProgress } from '@mui/material';
import { Search, WifiOff } from '@mui/icons-material';
import { CustomerDashboard } from "~/components/CustomerDashboard";
import { Container, Card } from "~/components/ui";
import {
  isMobileDevice,
  isValidBarcodeFormat,
  isValidPhoneFormat,
  normalizeScannedInput,
} from "~/lib/barcode/barcode-input.client";
import { searchRateLimiter, formatTimeRemaining } from "~/lib/utils/rate-limiter.client";
import { logScanAttempt } from "~/lib/offline/scan-audit.client";
import { syncPendingLogs } from "~/lib/offline/scan-audit.client";
import { useDevice } from "~/lib/auth/device-context";
import { searchCustomerAPI } from "~/lib/api/supabase-client";
import { BarcodeInput } from "~/components/BarcodeInput";
import { PhoneInput } from "~/components/PhoneInput";

interface SearchResponse {
  found: boolean;
  customer?: {
    id: string;
    barcode: string;
    name: string;
    phone: string;
    pointsBalance: number;
    isActive: boolean;
    createdAt: string;
    transactions?: any[];
    [key: string]: any;
  };
  transactions?: any[];
  message?: string;
}

export default function Index() {
  const { isRegistered, isLoading: isDeviceLoading, deviceToken } = useDevice();
  const [barcode, setBarcode] = useState("");
  const [phone, setPhone] = useState("");
  const [inputMode, setInputMode] = useState<"barcode" | "phone">("barcode");
  const [error, setError] = useState("");
  const [isMobile, setIsMobile] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [offlineQueue, setOfflineQueue] = useState<string[]>([]);

  // Active search state
  const [activeSearchTerm, setActiveSearchTerm] = useState<string | null>(null);
  const [viewOnly, setViewOnly] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  // Detect mobile device
  useEffect(() => {
    if (typeof window === "undefined") return;
    setIsMobile(isMobileDevice());
    setIsOnline(navigator.onLine);

    const handleOnline = () => {
      setIsOnline(true);
      if (offlineQueue.length > 0) syncOfflineQueue();
      syncPendingLogs().catch(() => { });
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Customer Query
  const { data: searchResult, isLoading: isSearchLoading, error: searchError, refetch } = useQuery({
    queryKey: ["customer", activeSearchTerm],
    queryFn: async () => {
      if (!activeSearchTerm) return null;

      console.log("[Customer Search] Fetching for:", activeSearchTerm);
      const data = await searchCustomerAPI(activeSearchTerm, deviceToken || undefined) as SearchResponse;
      return data;
    },
    enabled: !!activeSearchTerm && !!deviceToken,
    retry: false,
  });

  // Handle Search Result Side Effects
  useEffect(() => {
    if (searchResult) {
      if (!searchResult.found) {
        console.log("[Customer Search] Not found, redirecting...");
        const term = activeSearchTerm || "";
        const isPhone = isValidPhoneFormat(term);
        if (isPhone) {
          navigate(`/register?phone=${encodeURIComponent(term)}`);
        } else {
          navigate(`/register?barcode=${encodeURIComponent(term)}`);
        }
        setActiveSearchTerm(null); // Reset search
      } else {
        // Customer found
        console.log("[Customer Search] Found:", searchResult.customer);
        setError("");
      }
    }
  }, [searchResult, activeSearchTerm, navigate]);

  // Handle Search Error
  useEffect(() => {
    if (searchError) {
      console.error("[Customer Search] Error:", searchError);
      setError(searchError.message || "حدث خطأ أثناء البحث");
      setActiveSearchTerm(null);
    }
  }, [searchError]);

  const validateAndProcessSearch = useCallback(async (searchInput: string) => {
    const normalized = normalizeScannedInput(searchInput);
    if (!normalized) {
      setError("الرجاء إدخال الباركود أو رقم الهاتف");
      return;
    }

    // Rate Limit
    if (!searchRateLimiter.canAttempt("search")) {
      const timeRemaining = searchRateLimiter.getTimeUntilReset("search");
      setError(`تم تجاوز الحد المسموح للبحث. يرجى الانتظار ${formatTimeRemaining(timeRemaining)}`);
      return;
    }
    searchRateLimiter.recordAttempt("search");

    // Validate Format
    const isBarcode = isValidBarcodeFormat(normalized);
    const isPhone = isValidPhoneFormat(normalized);

    if (!isBarcode && !isPhone) {
      logScanAttempt(normalized, false, "Invalid format").catch(() => { });
      setError("تنسيق غير صحيح. يجب أن يكون باركود (R...) أو رقم هاتف (01...)");
      return;
    }

    // Set View Only mode for phone searches
    setViewOnly(isPhone);

    // Trigger Query
    setActiveSearchTerm(normalized);
    setError("");
  }, []);

  // Handle URL params to auto-search (e.g. after registration)
  useEffect(() => {
    const urlBarcode = searchParams.get("barcode");
    const urlPhone = searchParams.get("phone");
    if (urlBarcode) {
      setBarcode(urlBarcode);
      setInputMode("barcode");
      validateAndProcessSearch(urlBarcode);
    } else if (urlPhone) {
      setPhone(urlPhone);
      setInputMode("phone");
      validateAndProcessSearch(urlPhone);
    }
  }, [searchParams, validateAndProcessSearch]);

  const syncOfflineQueue = useCallback(async () => {
    if (offlineQueue.length === 0 || !navigator.onLine) return;

    console.log(`[Offline Queue] Syncing ${offlineQueue.length} items...`);
    const queueCopy = [...offlineQueue];
    setOfflineQueue([]);

    for (const barcode of queueCopy) {
      try {
        await validateAndProcessSearch(barcode);
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (e) {
        setOfflineQueue((prev) => [...prev, barcode]);
      }
    }
  }, [offlineQueue, validateAndProcessSearch]);

  // Keyboard Scanner Support
  useEffect(() => {
    if (typeof window === "undefined" || isMobile) return;

    let buffer = "";
    let lastKeyTime = 0;
    const BUFFER_TIMEOUT = 100;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (document.activeElement?.tagName !== "INPUT") return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const now = Date.now();
      if (now - lastKeyTime > BUFFER_TIMEOUT) buffer = "";
      lastKeyTime = now;

      if (e.key.length === 1 && !e.key.match(/[\x00-\x1F]/)) {
        buffer += e.key;
      } else if (e.key === "Enter" && buffer.length >= 11) {
        const normalized = normalizeScannedInput(buffer);
        if (isValidBarcodeFormat(normalized) || isValidPhoneFormat(normalized)) {
          validateAndProcessSearch(normalized);
        }
        buffer = "";
        return;
      }

      if (buffer.length >= 13) {
        const normalized = normalizeScannedInput(buffer);
        if (isValidBarcodeFormat(normalized)) {
          validateAndProcessSearch(normalized);
          buffer = "";
        }
      }
      if (buffer.length > 20) buffer = "";
    };

    window.addEventListener("keypress", handleKeyPress);
    return () => window.removeEventListener("keypress", handleKeyPress);
  }, [isMobile, validateAndProcessSearch]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const val = inputMode === "barcode" ? barcode : phone;
    if (val) validateAndProcessSearch(val);
  };

  const handleClear = () => {
    setActiveSearchTerm(null);
    setBarcode("");
    setPhone("");
    setError("");
    queryClient.removeQueries({ queryKey: ["customer"] });
  };

  const handleTransactionSuccess = () => {
    refetch();
  };

  const handleBarcodeChange = useCallback((value: string) => {
    setBarcode(value);
    if (isValidBarcodeFormat(value)) {
      setError("");
    }
  }, []);

  const handlePhoneChange = useCallback((value: string) => {
    setPhone(value);
    if (value.length === 11 && value.startsWith("01")) {
      setError("");
    }
  }, []);

  if (isDeviceLoading) {
    return (
      <Container size="xl" padding>
        <Card padding="lg">
          <Typography variant="body1" style={{ textAlign: 'center', display: 'block' }}>جاري التحقق من الجهاز...</Typography>
        </Card>
      </Container>
    );
  }

  if (!isRegistered) {
    return (
      <Container size="xl" padding>
        <Alert severity="warning">
          <AlertTitle>الجهاز غير مسجل</AlertTitle>
          <Typography variant="body2" sx={{ mb: 2 }}>
            يرجى زيارة صفحة الإعداد لتسجيل هذا الجهاز.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/setup')}
            size="small"
          >
            الانتقال إلى صفحة الإعداد
          </Button>
        </Alert>
      </Container>
    );
  }

  // If customer is found and we have data, show dashboard
  if (activeSearchTerm && searchResult?.found && searchResult.customer) {
    return (
      <Container size="xl" padding>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button variant="outlined" onClick={handleClear}>رجوع</Button>
        </Box>

        <CustomerDashboard
          customer={{
            ...searchResult.customer,
            transactions: searchResult.transactions
          } as any}
          viewOnly={viewOnly}
          onTransactionSuccess={handleTransactionSuccess}
        />
      </Container>
    );
  }

  return (
    <Container size="xl" padding>
      <Typography variant="h3" component="h1" sx={{ mb: 1 }}>
        نظام الكاش باك
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
        البحث عن عميل
      </Typography>

      {!isOnline && (
        <Alert severity="warning" icon={<WifiOff />} sx={{ mb: 2 }}>
          <AlertTitle>غير متصل</AlertTitle>
          {offlineQueue.length} عملية بحث في الانتظار
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSearchSubmit}
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 3,
          p: 3,
          boxShadow: 2,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <ToggleButtonGroup
            value={inputMode}
            exclusive
            onChange={(_, newMode) => {
              if (newMode) {
                setInputMode(newMode);
                setError('');
                if (newMode === 'barcode') setPhone('');
                else setBarcode('');
              }
            }}
            fullWidth
            sx={{ mb: 1 }}
          >
            <ToggleButton value="barcode">باركود</ToggleButton>
            <ToggleButton value="phone">رقم الهاتف</ToggleButton>
          </ToggleButtonGroup>

          <Box dir="ltr">
            {inputMode === 'barcode' ? (
              <BarcodeInput
                value={barcode}
                onChange={handleBarcodeChange}
                disabled={isSearchLoading}
                error={!!error}
                label="أدخل الباركود"
                helperText={error || "Format: R[YY][B][MM][C]M[CS1][CS2]"}
              />
            ) : (
              <PhoneInput
                value={phone}
                onChange={handlePhoneChange}
                disabled={isSearchLoading}
                error={!!error}
                label="أدخل رقم الهاتف"
                helperText={error}
              />
            )}
          </Box>

          {error && (
            <Alert severity="error">
              <AlertTitle>خطأ</AlertTitle>
              {error}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSearchLoading || (inputMode === 'barcode' ? !barcode : !phone)}
            fullWidth
            startIcon={isSearchLoading ? <CircularProgress size={20} /> : <Search />}
            sx={{ height: 56 }}
          >
            {isSearchLoading ? 'جاري البحث...' : 'بحث'}
          </Button>
        </Box>
      </Box>
    </Container>
  );
}
